import React from 'react';
import { Target, Eye, Award, Users } from 'lucide-react';
import { trustInfo } from '../mock';
import { Card, CardContent } from './ui/card';

export const About = () => {
  const highlights = [
    {
      icon: Target,
      title: 'Our Mission',
      description: trustInfo.mission,
      color: 'from-emerald-500 to-teal-600'
    },
    {
      icon: Eye,
      title: 'Our Vision',
      description: trustInfo.vision,
      color: 'from-teal-500 to-cyan-600'
    },
    {
      icon: Award,
      title: 'Our Approach',
      description: 'Combining sustainable business practices with compassionate social service to create lasting community impact.',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      icon: Users,
      title: 'Our Community',
      description: 'Building an inclusive ecosystem where every member contributes to and benefits from collective growth and prosperity.',
      color: 'from-blue-500 to-indigo-600'
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider mb-2 block">
              About Us
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Who We Are
            </h2>
            <div className="h-1 w-24 bg-gradient-to-r from-emerald-600 to-teal-600 mx-auto rounded-full"></div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Image */}
          <div className="relative">
            <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://res.cloudinary.com/db6zl4j0y/image/upload/v1769458883/logo-photoaidcom-cropped_be4sko.png"
                alt="Trust Building"
                className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
              />
            </div>
            {/* Floating Card */}
            <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                  <Award className="w-6 h-6 text-white" />
                </div>
                {/*<div>
                  <div className="text-2xl font-bold text-gray-900">15+ Years</div>
                  <div className="text-sm text-gray-600">Serving Community</div>
                </div>*/}
              </div>
            </div>
          </div>

          {/* Text Content */}
          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-gray-900">
              {trustInfo.name}
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed">
              {trustInfo.description}
            </p>
            <p className="text-gray-600 leading-relaxed">
              Founded in {trustInfo.established}, we have pioneered a unique model of community development 
              that transforms business success into social impact. Our diversified portfolio of enterprises 
              generates sustainable revenue streams that fund comprehensive welfare programs, education 
              initiatives, healthcare services, and community infrastructure projects.
            </p>
            <div className="flex items-start space-x-4 bg-emerald-50 rounded-xl p-5">
              <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Community-First Approach</h4>
                <p className="text-sm text-gray-600">
                  Every business decision we make is guided by its potential impact on community welfare 
                  and sustainable development.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Highlights Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {highlights.map((item, index) => (
            <Card 
              key={index} 
              className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden group"
            >
              <CardContent className="p-6">
                <div className={`w-12 h-12 bg-gradient-to-br ${item.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  {item.title}
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
